type LegacySearchPrefill = {
  loc?: string;
  locationType?: string;
  occasion?: string;
};

/**
 * Keys are compact (no spaces): legacy paths use `+` between words, e.g. `event+location+in+Berlin`
 * → "event location in Berlin" → keyword phrase "event location" → lookup as `eventlocation`.
 */
const LOCATION_TYPE_BY_KEYWORD: Record<string, string> = {
  eventlocation: "3",
  eventlocationen: "3",
  hochzeitslocation: "11",
  veranstaltungsraum: "4",
  tagungsraum: "9",
  seminarraum: "10",
  konferenzraum: "9",
  partylocation: "2",
  tagungshotel: "8",
};

const OCCASION_BY_KEYWORD: Record<string, string> = {
  feierlocation: "2",
  geburtstagslocation: "2",
  hochzeitsfeier: "1",
};

function decodeLegacySegment(segment: string): string {
  const plusAsSpace = segment.replace(/\+/g, " ");
  try {
    return decodeURIComponent(plusAsSpace).trim();
  } catch {
    return plusAsSpace.trim();
  }
}

function normalizeKeyword(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function extractCityFromPhrase(value: string): string | undefined {
  const match = /\bin\s+(.+)$/i.exec(value);
  return match?.[1]?.trim() || undefined;
}

function lookupCompactKey(map: Record<string, string>, normalized: string): string | undefined {
  const direct = map[normalized];
  if (direct !== undefined) return direct;
  const compact = normalized.replace(/\s+/g, "");
  return map[compact];
}

export function parseLegacySearchPathSegments(
  rawSegments: string[],
): LegacySearchPrefill | null {
  const segments = rawSegments.map(decodeLegacySegment).filter(Boolean);
  if (segments.length === 0) return null;

  const primaryPhrase = segments[0];
  const extractedCity = extractCityFromPhrase(primaryPhrase);

  const keywordPhrase = extractedCity
    ? primaryPhrase.slice(0, primaryPhrase.toLowerCase().lastIndexOf(" in ")).trim()
    : primaryPhrase.trim();
  const normalizedKeyword = normalizeKeyword(keywordPhrase);

  const locationType = lookupCompactKey(LOCATION_TYPE_BY_KEYWORD, normalizedKeyword);
  const occasion = lookupCompactKey(OCCASION_BY_KEYWORD, normalizedKeyword);
  const loc = extractedCity || segments[1];

  if (!loc && !locationType && !occasion) {
    return null;
  }

  return {
    ...(loc ? { loc } : {}),
    ...(locationType ? { locationType } : {}),
    ...(occasion ? { occasion } : {}),
  };
}
